import CommonUtils from "~/utils/common-utils";

const externalPrefixes = {
  woosmap: "https://api.woosmap.com/localities",
};

const externalEndpoints = {
  woosmap: {
    autocomplete: `${externalPrefixes.woosmap}/autocomplete/`,
    details: `${externalPrefixes.woosmap}/details/`,
    geocode: `${externalPrefixes.woosmap}/geocode/`,
  },
};

// Use ApiUtils.interpolateParams to replace your params dynamically
const endpoints = {
  captainWalletUrl: `loyalty/captain-wallet-url/`,
  socialWall: `social-wall/`,
  detectCountry: `edge/detect-country/`,
  contentfulTris: `contentful/tris/{locale}/{sku}/`,
};

const endpointsMapiV1 = {
  // AUTH
  ...{
    authCustomer: `auth/public/customer/`,
    authCustomerAvailableSubscriptions: `auth/public/customer/available-subscriptions/`,
    authCustomerEmailUpdate: `auth/public/customer/email/`,
    authCustomerLoyalty: `auth/public/customer/loyalty/`,
    authCustomerLoyaltyTiers: `auth/public/customer/loyalty-tiers/`,
    authCustomerPasswordReset: `auth/public/customer/password-reset/`,
    authCustomerSocial: `auth/public/customer/social/`,
    authCustomerSubscriptions: `auth/public/customer/subscriptions/`,
    authImpersonate: `auth/public/impersonate/`,
    authProspect: `auth/public/prospect/`,
    authSendEmailVerification: `auth/public/send-email-verification/`,
    authValidateEmail: `auth/public/validate-email/`,
  },
  // ECO
  ...{
    ecoContentfulCountryDialog: `eco/public/contentful/country-dialog/`,
    ecoContentfulCountryDialogAdmin: `eco/admin/contentful/country-dialog/`,
    ecoContentfulTranslations: `eco/public/contentful/translations/{locale}/`,
    ecoContentfulTranslationsAdmin: `eco/admin/contentful/translations/{locale}/`,
    ecoGuestTrackOrderAuth: `eco/public/guest-track-order/auth/`,
    ecoGuestTrackOrderCancel: `eco/public/guest-track-order/`,
    ecoGuestTrackOrderReturn: `eco/public/guest-track-order/customer-return/`,
    ecoGuestTrackOrderSummary: `eco/public/guest-track-order/`,
    ecoHelpCentreFaqSitemap: `eco/public/help-centre/faq/sitemap`,
    ecoLoyaltyPrefigureDiscounts: `eco/public/loyalty/prefigure-discounts/`,
    ecoOrder: `eco/public/order/{orderId}`,
    ecoReviewsBySku: `eco/reviews/reviews/{sku}`,
    ecoReviewsByCategory: `eco/public/category-summaries/{categoryPath}`,
    ecoReview: `eco/reviews/review/{reviewId}`,
    ecoReviewTranslation: `eco/reviews/review/{reviewId}/translate/{targetLanguage}`,
    ecoSocialWall: `eco/public/social-wall/`,
  },
  giftcardCheckBalance: `giftcard/check-balance`,
  optimalCarrierCarriers: `optimal-carrier/carriers/{country}/{zip}`,
  optimalCarrierPods: `optimal-carrier/pods/{country}/{city}/{zip}`,
};

type ApiEndpointKeys = keyof typeof endpoints;
type ApiEndpointKeysMapiV1 = keyof typeof endpointsMapiV1;

export class ApiConstants {
  static readonly headers = {
    X_CORRELATION_ID: "x-correlation-id",
    X_EVA_TOKEN: "x-eva-token",
    X_LOCALE: "x-locale",
    X_ORGANIZATION_UNIT_ID: "x-organization-unit-id",
  } as const;

  static get baseUrl(): string {
    return process.env.NEXT_PUBLIC_API_BASE_URL || "/api/next";
  }

  static get baseUrlMapiV1(): string {
    return process.env.NEXT_PUBLIC_MAPI_V1_BASE_URL || "/mapi/v1";
  }

  static get privateBaseUrl(): string {
    return CommonUtils.buildPath(this.baseUrl, "private");
  }

  // Used to decide whether request-only headers (correlation id, credentials, locale, ...) should be attached.
  static isInternalUrl(url: string): boolean {
    return (
      url.startsWith(process.env.NEXT_PUBLIC_API_V2_BASE_URL!) ||
      url.startsWith(process.env.NEXT_PUBLIC_MAPI_V1_BASE_URL!)
    );
  }

  static readonly externalEndpoints = externalEndpoints;

  static readonly endpoints: Record<ApiEndpointKeys, string> = this._mapEndpoints<ApiEndpointKeys>(
    endpoints,
    this.baseUrl
  );

  static readonly endpointsMapiV1: Record<ApiEndpointKeysMapiV1, string> = this._mapEndpoints<ApiEndpointKeysMapiV1>(
    endpointsMapiV1,
    this.baseUrlMapiV1
  );

  private static _mapEndpoints<T extends string = string>(endpointsMap: Record<T, string>, baseUrl: string) {
    return CommonUtils.mapObjectValues(endpointsMap, (v) => [baseUrl, v].join("/"));
  }
}
